/**
 * Setting or clearing a project's go-live/end date. SPEC-0007 Recommendation
 * §2's proposed resolution path: since these dates are normally set by ingest
 * data rather than a form, the one time a human edits them directly is to
 * clear a flagged disagreement — the next agreeing upload then simply
 * confirms the value rather than contesting it.
 *
 * Built on the native `<dialog>` element for the same reasons `ConfirmAction`
 * is: focus trapping, Escape-to-dismiss, and correct semantics for free.
 * Unlike `ConfirmAction` this isn't destructive — both fields are optional
 * and reversible — so there is no confirmation step, just Save and Cancel.
 */
import { useEffect, useRef, useState } from 'react';
import type { Project } from '@picklii/shared';
import { Icon } from './Icon';

export function EditProjectDatesDialog({
	project,
	busy,
	onSave,
}: {
	project: Project;
	busy: boolean;
	onSave(dates: { goLiveDate: string | null; endDate: string | null }): void;
}) {
	const dialog = useRef<HTMLDialogElement>(null);
	const [goLiveDate, setGoLiveDate] = useState(project.goLiveDate ?? '');
	const [endDate, setEndDate] = useState(project.endDate ?? '');

	useEffect(() => {
		if (!busy) dialog.current?.close();
	}, [busy]);

	function open() {
		// Re-seed from the current record every time it opens, not just on
		// mount — the project may have changed since (a new run, or a save
		// from a previous open of this same dialog).
		setGoLiveDate(project.goLiveDate ?? '');
		setEndDate(project.endDate ?? '');
		dialog.current?.showModal();
	}

	return (
		<>
			<button className="btn btn-sm btn-outline" onClick={open}>
				Edit dates
			</button>

			<dialog ref={dialog} className="modal">
				<div className="modal-box border-base-300 border text-start">
					<h3 className="text-base font-semibold">Lifecycle dates — {project.projectId}</h3>
					<p className="text-base-content/70 mt-2 text-sm">
						Both are optional. Normally set from a sold-hours upload; edit here mainly to
						resolve a flagged disagreement between two rows.
					</p>

					<div className="mt-4 flex flex-col gap-3">
						<div>
							<label className="floating-label">
								<span>Go-live date</span>
								<input
									type="date"
									className="input input-bordered w-full"
									value={goLiveDate}
									onChange={(e) => setGoLiveDate(e.target.value)}
								/>
							</label>
							{project.goLiveDateDisputed && (
								<p className="text-warning mt-1 flex items-center gap-1 text-xs">
									<Icon name="warning" className="size-3.5" />
									Uploads disagreed on this date — it was left unset.
								</p>
							)}
						</div>

						<div>
							<label className="floating-label">
								<span>End date</span>
								<input
									type="date"
									className="input input-bordered w-full"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
								/>
							</label>
							{project.endDateDisputed && (
								<p className="text-warning mt-1 flex items-center gap-1 text-xs">
									<Icon name="warning" className="size-3.5" />
									Uploads disagreed on this date — it was left unset.
								</p>
							)}
						</div>
					</div>

					<div className="modal-action">
						<button
							className="btn btn-sm btn-outline"
							disabled={busy}
							onClick={() => dialog.current?.close()}>
							Cancel
						</button>
						<button
							className="btn btn-sm btn-primary"
							disabled={busy}
							onClick={() =>
								onSave({
									goLiveDate: goLiveDate === '' ? null : goLiveDate,
									endDate: endDate === '' ? null : endDate,
								})
							}>
							{busy ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
						</button>
					</div>
				</div>

				<form method="dialog" className="modal-backdrop">
					<button aria-label="Cancel">close</button>
				</form>
			</dialog>
		</>
	);
}
